// ./src/components/features/PlanSelector/PlanSelector.tsx
import { useEffect, useMemo } from "react";
import {
  Group,
  Text,
  Badge,
  Stack,
  Loader,
  Alert,
  Button,
  Box,
  ThemeIcon,
  Center,
  Table,
  ScrollArea,
} from "@mantine/core";
import {
  IconCheck,
  IconX,
  IconCrown,
  IconInfoCircle,
  IconRefresh,
} from "@tabler/icons-react";
import { useAppPlans } from "@/hooks";
import { AppPlan, AppPlanPrice, AppFeature } from "@/utils/api";

export interface PlanSelectorProps {
  /** Current user's plan code */
  currentPlanId?: string | null | undefined;

  /** Date when trial ends (null if user is on paid plan) */
  trialEndsAt?: Date | null;

  /** Callback when user selects a plan */
  onChange: (plan: AppPlan, price: AppPlanPrice) => void;

  /** Show only active plans */
  showActiveOnly?: boolean;

  /** Loading state override */
  loading?: boolean;
}

const plansStatus = 100;

export const PlanSelector = ({
  currentPlanId,
  trialEndsAt,
  onChange,
  showActiveOnly = true,
  loading: externalLoading,
}: PlanSelectorProps) => {
  const { appPlans, loading, lastError, list, clearError } = useAppPlans({
    pageSize: 50,
    initialFilters: showActiveOnly ? { status: [plansStatus] } : {},
    autoLoad: false,
  });

  const isLoading = externalLoading || loading;
  const isOnTrial = trialEndsAt !== null && trialEndsAt !== undefined;
  const canSelectCurrentPlan = isOnTrial;

  // Load plans on component mount
  useEffect(() => {
    const filters = showActiveOnly ? { status: [plansStatus] } : {};
    list(filters, { page: 1, pageSize: 50 }, undefined, true);
  }, [list, showActiveOnly]);

  // Collect all unique features across all plans in order
  const allFeatures = useMemo(() => {
    const featuresMap = new Map<string, AppFeature>();
    const featureOrder: string[] = [];

    appPlans.forEach((plan) => {
      plan.planFeatures?.forEach((pf) => {
        if (pf.feature && !featuresMap.has(pf.feature.id)) {
          featuresMap.set(pf.feature.id, pf.feature);
          featureOrder.push(pf.feature.id);
        }
      });
    });

    return featureOrder.map((id) => featuresMap.get(id)!);
  }, [appPlans]);

  // Helper to parse feature value based on type
  const parseFeatureValue = (
    value: string | undefined,
    type: string | undefined | null
  ): any => {
    if (!value || !type) return null;

    try {
      switch (type) {
        case "boolean":
          return value.toLowerCase() === "true";
        case "integer":
          return parseInt(value, 10);
        case "decimal":
          return parseFloat(value);
        case "string[]":
        case "integer[]":
        case "decimal[]":
        case "boolean[]":
          return JSON.parse(value);
        case "string":
        default:
          return value;
      }
    } catch {
      return value;
    }
  };

  // Helper to get feature value for a plan
  const getFeatureValueForPlan = (plan: AppPlan, featureId: string) => {
    const planFeature = plan.planFeatures?.find(
      (pf) => pf.feature?.id === featureId
    );
    return planFeature?.featureValue;
  };

  // Helper to render feature value cell
  const renderFeatureCell = (
    value: string | undefined,
    type: string | undefined | null
  ) => {
    if (!value) {
      return (
        <Text c="dimmed" size="sm">
          —
        </Text>
      );
    }

    const parsedValue = parseFeatureValue(value, type);

    if (parsedValue === null || parsedValue === undefined) {
      return (
        <Text c="dimmed" size="sm">
          —
        </Text>
      );
    }

    if (typeof parsedValue === "boolean") {
      return parsedValue ? (
        <ThemeIcon variant="light" color="green" size="sm" radius="xl">
          <IconCheck size={14} />
        </ThemeIcon>
      ) : (
        <ThemeIcon variant="light" color="red" size="sm" radius="xl">
          <IconX size={14} />
        </ThemeIcon>
      );
    }

    if (Array.isArray(parsedValue)) {
      return (
        <Text size="sm" fw={500}>
          {parsedValue.join(", ")}
        </Text>
      );
    }

    return (
      <Text size="sm" fw={500}>
        {parsedValue.toLocaleString()}
      </Text>
    );
  };

  // Check if plan is current
  const isCurrentPlan = (planCode: string | undefined) =>
    planCode === currentPlanId;

  // Check if plan can be selected
  const canSelectPlan = (planCode: string | undefined) => {
    const isCurrent = isCurrentPlan(planCode);
    return !isCurrent || canSelectCurrentPlan;
  };

  if (isLoading) {
    return (
      <Center h={400}>
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text c="dimmed">Loading plans...</Text>
        </Stack>
      </Center>
    );
  }

  if (lastError) {
    return (
      <Alert
        variant="light"
        color="red"
        title="Error loading plans"
        icon={<IconInfoCircle size={16} />}
      >
        <Stack gap="sm">
          <Text>{lastError}</Text>
          <Group gap="xs">
            <Button
              variant="light"
              size="xs"
              leftSection={<IconRefresh size={14} />}
              onClick={() =>
                list(
                  showActiveOnly ? { status: [100] } : {},
                  { page: 1, pageSize: 50 },
                  undefined,
                  true
                )
              }
            >
              Retry
            </Button>
            <Button
              variant="subtle"
              size="xs"
              leftSection={<IconX size={14} />}
              onClick={clearError}
            >
              Dismiss
            </Button>
          </Group>
        </Stack>
      </Alert>
    );
  }

  if (appPlans.length === 0) {
    return (
      <Center h={200}>
        <Stack align="center" gap="md">
          <Text c="dimmed" size="lg">
            No plans available
          </Text>
          <Button
            variant="light"
            leftSection={<IconRefresh size={16} />}
            onClick={() =>
              list(
                showActiveOnly ? { status: [100] } : {},
                { page: 1, pageSize: 50 },
                undefined,
                true
              )
            }
          >
            Refresh
          </Button>
        </Stack>
      </Center>
    );
  }

  // Calculate column width based on number of plans
  const planColumnWidth = `${Math.floor(85 / appPlans.length)}%`;

  return (
    <Box>
      <ScrollArea>
        <Table
          withTableBorder
          withColumnBorders
          highlightOnHover
          verticalSpacing="md"
          horizontalSpacing="md"
          style={{ minWidth: 600 }}
        >
          {/* Header row with plan names and badges */}
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: "20%", minWidth: 200 }}>
                <Text fw={600} size="sm">
                  Features
                </Text>
              </Table.Th>
              {appPlans.map((plan) => {
                const isCurrent = isCurrentPlan(plan.planCode);
                const isPopular = plan.isPlanFeatured;

                return (
                  <Table.Th
                    key={plan.id}
                    style={{
                      width: planColumnWidth,
                      minWidth: 140,
                      backgroundColor: isCurrent
                        ? "var(--mantine-color-blue-0)"
                        : isPopular
                        ? "var(--mantine-color-orange-0)"
                        : undefined,
                      borderBottom: isCurrent
                        ? "3px solid var(--mantine-color-blue-6)"
                        : isPopular
                        ? "3px solid var(--mantine-color-orange-6)"
                        : undefined,
                    }}
                  >
                    <Stack gap="xs" align="center">
                      {/* Badges */}
                      {isPopular && !isCurrent && (
                        <Badge
                          variant="filled"
                          color="orange"
                          size="sm"
                          leftSection={<IconCrown size={10} />}
                        >
                          Popular
                        </Badge>
                      )}
                      {isCurrent && (
                        <Badge
                          variant="filled"
                          color="blue"
                          size="sm"
                          leftSection={<IconCheck size={10} />}
                        >
                          Current
                        </Badge>
                      )}
                      {!isPopular && !isCurrent && <Box h={22} />}

                      {/* Plan name */}
                      <Text fw={700} size="md" ta="center">
                        {plan.planName}
                      </Text>
                    </Stack>
                  </Table.Th>
                );
              })}
            </Table.Tr>

            {/* Pricing row */}
            <Table.Tr>
              <Table.Th>
                <Text fw={600} size="sm" c="dimmed">
                  Monthly Price
                </Text>
              </Table.Th>
              {appPlans.map((plan) => {
                const isCurrent = isCurrentPlan(plan.planCode);
                const isPopular = plan.isPlanFeatured;
                const planPrice = plan.prices?.find(
                  (p) => p.recurrence === "MONTHLY"
                );
                const monthlyPrice = planPrice?.price || 0;

                return (
                  <Table.Th
                    key={`${plan.id}-price`}
                    style={{
                      backgroundColor: isCurrent
                        ? "var(--mantine-color-blue-0)"
                        : isPopular
                        ? "var(--mantine-color-orange-0)"
                        : undefined,
                    }}
                  >
                    <Group justify="center" gap={4}>
                      <Text size="xl" fw={700} c="blue">
                        ${monthlyPrice}
                      </Text>
                      <Text c="dimmed" size="xs">
                        /mo
                      </Text>
                    </Group>
                  </Table.Th>
                );
              })}
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {/* Feature rows */}
            {allFeatures.map((feature) => (
              <Table.Tr key={feature.id}>
                <Table.Td>
                  <Text size="sm" fw={500}>
                    {feature.featureName || feature.featureCode}
                  </Text>
                  {feature.featureDescription && (
                    <Text size="xs" c="dimmed">
                      {feature.featureDescription}
                    </Text>
                  )}
                </Table.Td>
                {appPlans.map((plan) => {
                  const isCurrent = isCurrentPlan(plan.planCode);
                  const isPopular = plan.isPlanFeatured;
                  const value = getFeatureValueForPlan(plan, feature.id);

                  return (
                    <Table.Td
                      key={`${plan.id}-${feature.id}`}
                      style={{
                        textAlign: "center",
                        backgroundColor: isCurrent
                          ? "var(--mantine-color-blue-0)"
                          : isPopular
                          ? "var(--mantine-color-orange-0)"
                          : undefined,
                      }}
                    >
                      <Center>
                        {renderFeatureCell(value, feature.featureValueType)}
                      </Center>
                    </Table.Td>
                  );
                })}
              </Table.Tr>
            ))}

            {/* Action row */}
            <Table.Tr>
              <Table.Td />
              {appPlans.map((plan) => {
                const isCurrent = isCurrentPlan(plan.planCode);
                const isPopular = plan.isPlanFeatured;
                const planPrice = plan.prices?.find(
                  (p) => p.recurrence === "MONTHLY"
                );

                if (!planPrice) return <Table.Td key={plan.id} />;

                return (
                  <Table.Td
                    key={`${plan.id}-action`}
                    style={{
                      backgroundColor: isCurrent
                        ? "var(--mantine-color-blue-0)"
                        : isPopular
                        ? "var(--mantine-color-orange-0)"
                        : undefined,
                    }}
                  >
                    <Stack gap="xs" align="center">
                      <Button
                        fullWidth
                        variant={isCurrent ? "light" : "filled"}
                        color="blue"
                        disabled={!canSelectPlan(plan.planCode)}
                        onClick={() => onChange(plan, planPrice)}
                        size="sm"
                      >
                        {isCurrent
                          ? canSelectCurrentPlan
                            ? "Continue"
                            : "Current"
                          : "Select"}
                      </Button>
                      {isCurrent && !canSelectCurrentPlan && (
                        <Text size="xs" c="dimmed" ta="center">
                          Active plan
                        </Text>
                      )}
                    </Stack>
                  </Table.Td>
                );
              })}
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </Box>
  );
};
